/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { Card, Grid, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as Cesium from "cesium";
import { Map } from "ol";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { classNames } from "react-extras";
import {
  SeaSurveyListDto,
  useDesignMaterialApi,
  useGeopickingApi,
  useMap3DObjectApi,
} from "~/api/GeneratedApi";
import {
  setCesiumPreloadMeas,
  setCesiumState,
  setMapType,
  setSelectedPointcloud,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import {
  readMapSettings,
  updateMapSettings,
} from "../functions/localMapSettings";
import CesiumTilesetAdjustment from "./components/adjustment/CesiumTilesetAdjustments";
import { TerrainAdjustmentDialog } from "./components/adjustment/TerrainAdjustmenDialog";
import { CesiumCoordinateTooltip } from "./components/CesiumCoordinateTooltip";
import CesiumDrawer from "./components/CesiumDrawer";
import CesiumPopups from "./components/CesiumPopups";
import CesiumToolbar from "./components/CesiumToolbar";
import { cadToCesium } from "./functions/cadToCesium";
import { geopickingToCesium } from "./functions/geopickingToCesium";
import { getEntityByMapId } from "./functions/getEntityByMapId";
import { getTileset } from "./functions/getTileset";
import { map3dObjectsToCesium } from "./functions/map3dObjectsToCesium";
import { measurementInfoToCesium } from "./functions/measurementInfoToCesium";
import { olViewToCesium } from "./functions/olViewToCesium";
import {
  CesiumPopupOriginalPosition,
  useCesiumEventHandler,
} from "./hooks/useCesiumEventHandler";
import { useCesiumTilesetLoader } from "./hooks/useCesiumTilesetLoader";
import { Custom3DTileset } from "./types/CesiumMapItems";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    "& .cesium-viewer": {
      "& .cesium-viewer-toolbar": {
        right: theme.spacing(10),
        [theme.breakpoints.down("lg")]: {
          right: theme.spacing(1),
        },
      },
    },
  },
  popup: {
    position: "absolute",
    bottom: 0,
    left: 0,
    overflow: "visible",
    "&:after": {
      border: "solid transparent",
      content: '""',
      height: 0,
      width: 0,
      position: "absolute",
      pointerEvents: "none",
      borderTopColor: theme.palette.background.paper,
      borderWidth: 10,
      left: 10,
      zIndex: 1001,
    },
    "& .MuiCardHeader-content": {
      maxWidth: "min(400px, 60vw)",
    },
  },
}));

type Props = {
  map: Map;
  measurements?: SeaSurveyListDto[];
};

type MouseMoveEvent = {
  startPosition: Cesium.Cartesian2;
  endPosition: Cesium.Cartesian2;
};

const containerId = "cesium-container";

const CesiumMap = ({ map, measurements }: Props): JSX.Element | null => {
  const classes = useStyles();
  const viewerRef = useRef<Cesium.Viewer>();
  const cameraRef = useRef<Cesium.Camera>();
  const sceneRef = useRef<Cesium.Scene>();
  const globeRef = useRef<Cesium.Globe>();
  const [initialTilesLoaded, setInitialTilesLoaded] = useState(false);
  const [pinned, setPinned] = useState(readMapSettings("cesiumDrawerPinned"));
  const [measInfoSet, setMeasInfoSet] = useState(false);
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const [popupPosLocal, setPopupPosLocal] = React.useState<{
    left: number;
    bottom: number;
  }>();

  const isMobile = useAppSelector((s) => s.general.isMobile);
  const mapType = useAppSelector((s) => s.map.mapType);
  const cesiumState = useAppSelector((s) => s.map.cesiumState);

  const preloadMeas = useAppSelector((s) => s.map.cesiumPreloadMeas);
  const popupPosition = useAppSelector((s) => s.map.cesiumPopupPosition);
  const editPointclouds = useAppSelector((s) => s.map.selectedPointclouds);
  const hoveredCesiumItem = useAppSelector((s) => s.map.hoveredCesiumItem);
  const selectedCesiumItem = useAppSelector((s) => s.map.selectedCesiumItem);
  const hoveredId = useMemo(
    () => hoveredCesiumItem?.mapId,
    [hoveredCesiumItem],
  );
  const selectedId = useMemo(
    () => selectedCesiumItem?.mapId,
    [selectedCesiumItem],
  );
  const dispatch = useAppDispatch();
  const { cesiumEventHandler } = useCesiumEventHandler();
  const { loader, tileset, resetTileset } = useCesiumTilesetLoader();
  const { getGeopickings } = useGeopickingApi();
  const { getAvailableObjects } = useMap3DObjectApi();
  const { getDesignMaterials } = useDesignMaterialApi();
  const { data: geopicking } = useCustomQuery("geopicking", () =>
    getGeopickings(),
  );
  const { data: map3dObjects } = useCustomQuery("map-3d-objects", () =>
    getAvailableObjects(),
  );
  const { data: designMaterials } = useCustomQuery("design-materials", () =>
    getDesignMaterials(),
  );

  // Sync popup pos from redux
  useEffect(() => {
    setPopupPosLocal(popupPosition);
  }, [popupPosition]);

  // Add measurements to drawer and to map
  useEffect(() => {
    if (!viewerRef.current || cesiumState !== "ready" || !measurements) return;
    measurementInfoToCesium(viewerRef.current, measurements);
    setMeasInfoSet(true);
  }, [measurements, cesiumState]);

  // Add geopicking to map
  useEffect(() => {
    if (!viewerRef.current || cesiumState !== "ready" || !geopicking?.results)
      return;
    geopickingToCesium(viewerRef.current, geopicking.results);
  }, [geopicking, cesiumState]);

  // Add terrain and imagery to map
  useEffect(() => {
    if (!viewerRef.current || cesiumState !== "ready" || !map3dObjects?.results)
      return;
    map3dObjectsToCesium(viewerRef.current, map3dObjects.results);
  }, [cesiumState, map3dObjects?.results]);

  // Add cad (design materials) to map
  useEffect(() => {
    if (
      !viewerRef.current ||
      cesiumState !== "ready" ||
      !designMaterials?.results
    )
      return;
    cadToCesium(viewerRef.current, designMaterials.results);
  }, [cesiumState, designMaterials?.results]);

  // Preload a measurement
  useEffect(() => {
    if (!measInfoSet || !preloadMeas || !viewerRef.current || !measurements) {
      return;
    }

    const meas = measurements.find(
      (m) => preloadMeas.mapId === `measurement${m.id}`,
    );
    if (!meas) return;
    const { files } = meas;
    if (!files?.length) return;
    files.forEach((f) => {
      if (viewerRef.current) {
        loader({
          viewer: viewerRef.current,
          file: f,
          markerMapId: preloadMeas?.mapId,
          projection: meas.coordinateSystem?.name ?? "",
          seaSurvey: meas,
        });
      }
    });
  }, [loader, measInfoSet, measurements, preloadMeas]);

  // Move back to 2D when unmounting
  React.useEffect(() => {
    return () => {
      dispatch(setMapType("2d"));
      dispatch(setCesiumState("unloaded"));
      dispatch(setSelectedPointcloud(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!tileset || !preloadMeas) return;
    viewerRef.current?.zoomTo(tileset);
    dispatch(setCesiumPreloadMeas(undefined));
  }, [preloadMeas, tileset, dispatch]);

  const handlePin = useCallback(() => {
    setPinned((prev) => {
      const val = isMedium ? false : !prev;
      updateMapSettings({ cesiumDrawerPinned: val });
      return val;
    });
  }, [isMedium]);

  useEffect(() => {
    if (mapType !== "3d") return;
    dispatch(setCesiumState("loading"));
  }, [dispatch, mapType]);

  const initCamera = useCallback(() => {
    if (!cameraRef.current || !sceneRef.current) return;
    const init = async () => {
      await olViewToCesium(
        map.getView(),
        cameraRef.current!,
        sceneRef.current!,
      );
    };
    init();
  }, [map]);

  useEffect(() => {
    if (!initialTilesLoaded) return;
    dispatch(setCesiumState("ready"));
    if (globeRef.current) {
      if (sceneRef.current) {
        sceneRef.current.screenSpaceCameraController.enableCollisionDetection =
          false;
      }
      globeRef.current.translucency.enabled = true;
      globeRef.current.translucency.frontFaceAlphaByDistance =
        new Cesium.NearFarScalar(2, 0.0, 5000, 1.0);
    }
  }, [initialTilesLoaded, dispatch]);

  // Create cesium and other necessary components and get refs of them
  useEffect(() => {
    if (viewerRef.current || mapType !== "3d") return;

    // Static time
    const summerTime = new Date(Date.UTC(2021, 6, 10, 14, 30));
    const julianDate = Cesium.JulianDate.fromDate(summerTime);
    const clock = new Cesium.Clock({
      startTime: julianDate,
      currentTime: julianDate,
      multiplier: 0,
      shouldAnimate: false,
    });

    viewerRef.current = new Cesium.Viewer(containerId, {
      timeline: false,
      animation: false,
      fullscreenButton: false,
      homeButton: false,
      geocoder: false,
      clockViewModel: new Cesium.ClockViewModel(clock),
      // terrainProvider: new Cesium.CesiumTerrainProvider({
      //   url: Cesium.IonResource.fromAssetId(1),
      //   requestVertexNormals: true,
      //   requestWaterMask: true,
      // }),
      sceneModePicker: false,
      infoBox: false,
      selectionIndicator: false,
      // globe: false,
      // skyAtmosphere: false,
      // skyBox: false,
    });
    sceneRef.current = viewerRef.current.scene;
    cameraRef.current = viewerRef.current.camera;
    globeRef.current = viewerRef.current.scene.globe;
    sceneRef.current.screenSpaceCameraController.enableCollisionDetection =
      false;

    // Watch the tile queue for the initial tile load
    const onTilesLoaded = (queueLength: number) => {
      if (queueLength !== 0) return;
      setInitialTilesLoaded(true);
    };
    globeRef.current.tileLoadProgressEvent.addEventListener(onTilesLoaded);
    // dispatch(setCesiumState("ready"));
  }, [mapType, dispatch]);

  // Cleanup
  useEffect(() => {
    if (mapType !== "3d") {
      viewerRef.current?.destroy();
      viewerRef.current = undefined;
      sceneRef.current = undefined;
      cameraRef.current = undefined;
      globeRef.current = undefined;
      resetTileset();
      setMeasInfoSet(false);
      setInitialTilesLoaded(false);
    }
  }, [mapType, resetTileset]);

  // event handlers
  useEffect(() => {
    if (mapType !== "3d" || !viewerRef.current) return;

    viewerRef.current.screenSpaceEventHandler.removeInputAction(
      Cesium.ScreenSpaceEventType.LEFT_CLICK,
    );
    viewerRef.current.screenSpaceEventHandler.removeInputAction(
      Cesium.ScreenSpaceEventType.MOUSE_MOVE,
    );

    viewerRef.current.screenSpaceEventHandler.setInputAction((e) => {
      if (!viewerRef.current || !sceneRef.current) return;
      const { position } = e;
      cesiumEventHandler({
        position,
        viewer: viewerRef.current,
        scene: sceneRef.current,
        eventType: "click",
        hoveredId,
        selectedId,
      });
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    viewerRef.current.screenSpaceEventHandler.setInputAction((e) => {
      if (!viewerRef.current || !sceneRef.current) return;
      cesiumEventHandler({
        position: (e as MouseMoveEvent)?.endPosition,
        viewer: viewerRef.current,
        scene: sceneRef.current,
        eventType: "hover",
        hoveredId,
        selectedId,
      });
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }, [mapType, hoveredId, selectedId, cesiumEventHandler]);

  useEffect(() => {
    const temp2dPosition = new Cesium.Cartesian2();
    const now = Cesium.JulianDate.now();
    const entity =
      selectedId && viewerRef.current
        ? getEntityByMapId(viewerRef.current, selectedId)
        : undefined;
    const pos3d = entity ? entity.position?.getValue(now) : undefined;
    const props = entity?.properties?.getValue(now);
    const popupPosOriginal = props
      ? props[CesiumPopupOriginalPosition]
      : undefined;
    const listener = () => {
      if (!selectedId || !popupPosition || !viewerRef.current) return;
      if (!pos3d && !popupPosOriginal) return;
      const pos2d = Cesium.SceneTransforms.wgs84ToWindowCoordinates(
        viewerRef.current.scene,
        pos3d ??
          new Cesium.Cartesian3(
            popupPosOriginal.x,
            popupPosOriginal.y,
            popupPosOriginal.z,
          ),
        temp2dPosition,
      );
      const markerSize = 48;
      const arrowSize = 10;
      const pos =
        selectedId === "geopicking-new"
          ? {
              bottom:
                viewerRef.current.canvas.clientHeight - pos2d.y + arrowSize,
              left: pos2d.x - arrowSize * 2,
            }
          : {
              bottom: pos3d
                ? viewerRef.current.canvas.clientHeight -
                  pos2d.y +
                  markerSize +
                  arrowSize
                : viewerRef.current.canvas.clientHeight - pos2d.y + arrowSize,
              left: pos2d.x - markerSize / 2 + arrowSize / 2,
            };
      setPopupPosLocal(pos);
    };

    viewerRef.current?.clock.onTick.addEventListener(listener);
    return () => {
      viewerRef.current?.clock.onTick.removeEventListener(listener);
    };
  }, [popupPosition, selectedId]);

  // Initialize camera extent to same location as the map was
  useEffect(() => {
    if (mapType !== "3d") return;
    initCamera();
  }, [mapType, initCamera]);

  const tilesetsToAdjust: Custom3DTileset[] = useMemo(() => {
    const sets: Custom3DTileset[] = [];
    editPointclouds?.forEach((x) => {
      if (viewerRef.current) {
        const ts = getTileset(x, viewerRef.current);
        if (ts) {
          sets.push(ts);
        }
      }
    });
    return sets;
  }, [editPointclouds]);

  if (mapType !== "3d") return null;

  return (
    <Grid container>
      {viewerRef.current ? <CesiumToolbar viewer={viewerRef.current} /> : null}
      <Grid
        item
        xs
        id={containerId}
        style={{
          width: "100%",
          height: isMobile ? "var(--content)" : "var(--full-vh, 100vh)",
          position: "relative",
        }}
        className={classNames({ [classes.toolbar]: !pinned })}
      >
        <Card
          id="cesium-overlay"
          className={classes.popup}
          style={{
            bottom: popupPosLocal?.bottom,
            left: popupPosLocal?.left,
            display: popupPosLocal ? "block" : "none",
            zIndex: 1000,
          }}
        >
          {viewerRef.current && <CesiumPopups viewer={viewerRef.current} />}
        </Card>
        <CesiumCoordinateTooltip />
      </Grid>
      {viewerRef.current && cesiumState === "ready" && (
        <>
          <CesiumDrawer
            onPin={handlePin}
            pinned={pinned}
            viewer={viewerRef.current}
          />
          <CesiumTilesetAdjustment
            tilesets={tilesetsToAdjust}
            open={!!editPointclouds?.length}
          />
          <TerrainAdjustmentDialog viewer={viewerRef.current} />
        </>
      )}
    </Grid>
  );
};

export default CesiumMap;
